import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import taskConfig from './taskConfig.json';
import AgentForm from './AgentForm';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import GeneratePromptModal from './components/GeneratePromptModal';
import { SocialIcon } from 'react-social-icons';
import Login from './components/Login';
import Callback from './components/Callback';
import Account from './components/Account';
import AuthOptions from './components/AuthOptions';
import FreeTier from './components/FreeTier';
import CreateAccount from './components/CreateAccount';
import PrivacyPolicy from './components/PrivacyPolicy';

function Header() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);
  
  const handleLogout = () => {
    // Clear all user-related data from localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('introSeen');
    // Reset the user state
    setUser(null);
    // Navigate to home page
    navigate('/');
  };
  
  return (
    <header className="bg-white shadow-sm sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-[#0078D4]">CoPilot Prompt Generator</h1>
        <nav>
          <ul className="flex space-x-6">
            <li>
              <Link to="/" className="text-gray-600 hover:text-[#0078D4]">Home</Link>
            </li>
            {user ? (
              <>
                {user.tier === 'paid' ? (
                  <li>
                    <Link to="/account" className="text-gray-600 hover:text-[#0078D4]">Account (Premium)</Link>
                  </li>
                ) : (
                  <>
                    <li>
                      <Link to="/login" className="text-gray-600 hover:text-[#0078D4]">Login</Link>
                    </li>
                    <li>
                      <Link to="/createaccount" className="text-gray-600 hover:text-[#0078D4]">Create Account</Link>
                    </li>
                  </>
                )}
                <li>
                  <button 
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-[#0078D4]"
                  >
                    Clear Session
                  </button>
                </li>
              </>
            ) : (
              <li>
                <Link to="/login" className="text-gray-600 hover:text-[#0078D4]">Login</Link>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
}

function PromptForm({ onGenerate }) {
  const defaultTask = Object.keys(taskConfig)[0];
  
  // Initialize default options based on the first task
  const initialOptions = {};
  taskConfig[defaultTask].optionCategories.forEach(category => {
    initialOptions[category.name] = category.options[0];
  });
  
  const [formData, setFormData] = useState({
    task: defaultTask,
    options: initialOptions,
    complexity: taskConfig[defaultTask].complexity.length > 0 ? taskConfig[defaultTask].complexity[0] : '',
    keywords: '',
    context: ''
  });
  
  const [keywordPills, setKeywordPills] = useState([]);
  const [contextPills, setContextPills] = useState([]);
  const [keywordError, setKeywordError] = useState('');
  const [contextError, setContextError] = useState('');
  const [showKeywordTooltip, setShowKeywordTooltip] = useState(false);
  const [showContextTooltip, setShowContextTooltip] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, stage: 'initial', content: null, position: { x: 0, y: 0 }, timers: { initial: null, expanded: null } });

  const handleChange = (e) => {
    const { id, value } = e.target;
    
    if (id === 'task') {
      // Reset options when task changes
      const newOptions = {};
      taskConfig[value].optionCategories.forEach(category => {
        newOptions[category.name] = category.options[0];
      });
      
      // Initialize complexity if applicable
      const newComplexity = taskConfig[value].complexity.length > 0 
        ? taskConfig[value].complexity[0] 
        : '';
      
      setFormData({
        ...formData,
        task: value,
        options: newOptions,
        complexity: newComplexity
      });
      
      // Reset pills to avoid inconsistency with new task
      setKeywordPills([]);
      setContextPills([]);
    } 
    else if (id === 'complexity') {
      setFormData({
        ...formData,
        complexity: value
      });
    }
    else if (id === 'keywords' || id === 'context') {
      setFormData({
        ...formData,
        [id]: value
      });
    }
    else {
      // Handle option category changes
      setFormData({
        ...formData,
        options: {
          ...formData.options,
          [id]: value
        }
      });
    }
  };

  const handleTaskSelect = (task) => {
    // Simulate the behavior of handleChange for task selection
    const newOptions = {};
    taskConfig[task].optionCategories.forEach(category => {
      newOptions[category.name] = category.options[0];
    });
    
    const newComplexity = taskConfig[task].complexity.length > 0 
      ? taskConfig[task].complexity[0] 
      : '';
    
    setFormData({
      ...formData,
      task: task,
      options: newOptions,
      complexity: newComplexity
    });
    
    setKeywordPills([]);
    setContextPills([]);
    setIsTaskDropdownOpen(false);
  };

  const handleMouseEnter = (e, task) => {
    const rect = e.target.getBoundingClientRect();
    // Assuming taskConfig[task] has tooltip and example fields
    const tooltipData = taskConfig[task].tooltip || "Tooltip information for " + task;
    const exampleData = taskConfig[task].example || "Example for " + task;
    
    // Clear any existing timers
    if (tooltip.timers.initial) clearTimeout(tooltip.timers.initial);
    if (tooltip.timers.expanded) clearTimeout(tooltip.timers.expanded);
    
    // Set initial tooltip after a short delay (300ms)
    const initialTimer = setTimeout(() => {
      setTooltip(prev => ({
        ...prev,
        visible: true,
        stage: 'initial',
        content: { tooltip: tooltipData, example: exampleData },
        position: { x: rect.right + 10, y: rect.top }
      }));
    }, 300);
    
    // Expand tooltip to show example after a longer delay (1000ms total)
    const expandedTimer = setTimeout(() => {
      setTooltip(prev => ({
        ...prev,
        stage: prev.visible ? 'expanded' : prev.stage
      }));
    }, 1000);
    
    setTooltip(prev => ({
      ...prev,
      timers: { initial: initialTimer, expanded: expandedTimer }
    }));
  };

  const handleMouseLeave = () => {
    // Clear timers if they exist
    if (tooltip.timers.initial) clearTimeout(tooltip.timers.initial);
    if (tooltip.timers.expanded) clearTimeout(tooltip.timers.expanded);
    
    setTooltip({ visible: false, stage: 'initial', content: null, position: { x: 0, y: 0 }, timers: { initial: null, expanded: null } });
  };

  const handleKeywordSubmit = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      e.preventDefault();
      const text = formData.keywords.trim();
      if (text) {
        if (/^[a-zA-Z]+$/.test(text)) {
          setKeywordPills([...keywordPills, text]);
          setKeywordError('');
          setFormData({ ...formData, keywords: '' });
        } else {
          setKeywordError('Only alphabetic characters (a-z, A-Z) are allowed.');
        }
      }
    }
  };

  const handleContextSubmit = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      e.preventDefault();
      const text = formData.context.trim();
      if (text) {
        if (/^[a-zA-Z\s]+$/.test(text) && text.length <= 250) {
          setContextPills([...contextPills, text]);
          setContextError('');
          setFormData({ ...formData, context: '' });
        } else {
          setContextError('Only alphabetic characters (a-z, A-Z) and spaces are allowed, up to 250 characters.');
        }
      }
    }
  };

  const removeKeywordPill = (index) => {
    setKeywordPills(keywordPills.filter((_, i) => i !== index));
  };

  const removeContextPill = (index) => {
    setContextPills(contextPills.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (taskConfig[formData.task].additionalContext === 'mandatory' && contextPills.length === 0) {
      setContextError('Additional context is required for this task type.');
      return;
    }
    
    setIsGenerating(true);
    
    const submissionData = {
      task: formData.task,
      options: formData.options,
      keywords: keywordPills.join(', '),
      context: contextPills.join('. ')
    };

    if (taskConfig[formData.task].complexity.length > 0) {
      submissionData.complexity = formData.complexity;
    }
    
    onGenerate(submissionData, () => setIsGenerating(false));
  };

  const currentTaskConfig = taskConfig[formData.task];

  return (
    <section id="generator" className="bg-white rounded-lg p-6 max-w-2xl mx-auto shadow-[0px_0px_10px_rgba(0,0,0,0.9)]">
      <h3 className="text-2xl font-semibold text-gray-800 mb-4">Create Your Prompt</h3>
      <form onSubmit={handleSubmit}>
        <div className="mb-4 relative">
          <label className="block text-gray-700 mb-2" htmlFor="task">Task Type</label>
          <button
            type="button"
            onClick={() => setIsTaskDropdownOpen(!isTaskDropdownOpen)}
            className="w-full bg-white text-left p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4] flex justify-between items-center"
          >
            <span>{formData.task}</span>
            <span className="text-gray-500">{isTaskDropdownOpen ? '▲' : '▼'}</span>
          </button>
          
          {isTaskDropdownOpen && (
            <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {Object.keys(taskConfig).map(task => (
                <li
                  key={task}
                  className="p-2 hover:bg-blue-50 cursor-pointer"
                  onMouseEnter={(e) => handleMouseEnter(e, task)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleTaskSelect(task)}
                >
                  {task}
                </li>
              ))}
            </ul>
          )}
          
          {tooltip.visible && tooltip.content && (
            <div
              className="fixed z-20 bg-gray-900 bg-opacity-90 text-white rounded-xl shadow-2xl max-w-md p-6 transition-all duration-300 ease-in-out"
              style={{ top: tooltip.position.y, left: tooltip.position.x, minHeight: tooltip.stage === 'expanded' ? '200px' : '120px' }}
            >
              <div className="border-b border-gray-500 pb-3 mb-3">
                <h4 className="text-lg font-semibold mb-1">About this Task</h4>
                <p className="text-base leading-relaxed">{tooltip.content.tooltip}</p>
              </div>
              {tooltip.stage === 'expanded' && (
                <div className="animate-fade-in">
                  <h4 className="text-lg font-semibold mb-1">Example</h4>
                  <p className="text-base leading-relaxed italic">{tooltip.content.example}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Render option categories dynamically */}
        {currentTaskConfig.optionCategories.map(category => (
          <div className="mb-4" key={category.name}>
            <label className="block text-gray-700 mb-2" htmlFor={category.name}>
              {category.label}
            </label>
            <select 
              id={category.name} 
              value={formData.options[category.name] || ''} 
              onChange={handleChange} 
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
            >
              {category.options.map(option => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>
        ))}
        
        {/* Render complexity if applicable */}
        {currentTaskConfig.complexity.length > 0 && (
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="complexity">Complexity</label>
            <select 
              id="complexity" 
              value={formData.complexity} 
              onChange={handleChange} 
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
            >
              {currentTaskConfig.complexity.map(option => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Keywords section */}
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="keywords">
            Keywords {currentTaskConfig.keywords === 'optional' ? '(optional)' : '(required)'}
          </label>
          <div className="flex flex-wrap gap-2 mb-2 p-2 border rounded-md min-h-[40px]">
            {keywordPills.map((pill, index) => (
              <span key={index} className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                {pill}
                <button type="button" onClick={() => removeKeywordPill(index)} className="text-white hover:text-red-200 focus:outline-none">x</button>
              </span>
            ))}
          </div>
          <div className="relative flex items-center group">
            <input 
              id="keywords" 
              type="text" 
              value={formData.keywords} 
              onChange={handleChange} 
              onKeyPress={handleKeywordSubmit} 
              onBlur={handleKeywordSubmit} 
              placeholder="e.g., meeting agenda, urgent" 
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]" 
            />
            <button
              type="button"
              aria-label="Show tooltip"
              className="ml-2 text-gray-400 hover:text-[#0078D4] focus:outline-none"
              onClick={() => setShowKeywordTooltip(true)}
            >
              ?
            </button>
            {showKeywordTooltip && (
              <div className="absolute left-0 top-[-3.5rem] bg-gray-800 text-white text-xs rounded-xl py-2 px-4 z-20 shadow-lg flex items-center animate-fade-in"
                   style={{ minWidth: '260px' }}>
                <span>
                  Enter a single word. Press Enter or Tab or Click Away to create a tag. a-z, A-Z only, special characters and spaces are not permitted.
                </span>
                <button
                  type="button"
                  aria-label="Close tooltip"
                  className="ml-3 text-white hover:text-red-300 text-lg font-bold focus:outline-none"
                  onClick={() => setShowKeywordTooltip(false)}
                  style={{ lineHeight: '1' }}
                >
                  ×
                </button>
                <span className="absolute left-4 -bottom-2 w-3 h-3 bg-gray-800 rotate-45 shadow-lg"></span>
              </div>
            )}
          </div>
          {keywordError && <p className="text-red-500 text-sm mt-2">{keywordError}</p>}
        </div>
        
        {/* Additional Context section */}
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="context">
            Additional Context {currentTaskConfig.additionalContext === 'mandatory' ? '(required)' : '(optional)'}
          </label>
          <div className="flex flex-wrap gap-2 mb-2 p-2 border rounded-md min-h-[40px]">
            {contextPills.map((pill, index) => (
              <span key={index} className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                {pill}
                <button type="button" onClick={() => removeContextPill(index)} className="text-white hover:text-red-200 focus:outline-none">x</button>
              </span>
            ))}
          </div>
          <div className="relative flex items-center group">
            <textarea 
              id="context" 
              value={formData.context} 
              onChange={handleChange} 
              onKeyPress={handleContextSubmit} 
              onBlur={handleContextSubmit} 
              placeholder="e.g., For a marketing team meeting" 
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4]" 
              rows="4"
              maxLength="250"
            ></textarea>
            <button
              type="button"
              aria-label="Show tooltip"
              className="ml-2 text-gray-400 hover:text-[#0078D4] focus:outline-none"
              onClick={() => setShowContextTooltip(true)}
            >
              ?
            </button>
            {showContextTooltip && (
              <div className="absolute left-0 top-[-3.5rem] bg-gray-800 text-white text-xs rounded-xl py-2 px-4 z-20 shadow-lg flex items-center animate-fade-in"
                   style={{ minWidth: '260px' }}>
                <span>
                  Enter a phrase then press Enter or Tab or Click Away to create a tag. a-z, A-Z and spaces only, max 250 chars
                </span>
                <button
                  type="button"
                  aria-label="Close tooltip"
                  className="ml-3 text-white hover:text-red-300 text-lg font-bold focus:outline-none"
                  onClick={() => setShowContextTooltip(false)}
                  style={{ lineHeight: '1' }}
                >
                  ×
                </button>
                <span className="absolute left-4 -bottom-2 w-3 h-3 bg-gray-800 rotate-45 shadow-lg"></span>
              </div>
            )}
          </div>
          <p className="text-gray-500 text-sm mt-1">{formData.context.length}/250 characters</p>
          {contextError && <p className="text-red-500 text-sm mt-2">{contextError}</p>}
        </div>
        <button 
          type="submit" 
          className="w-full bg-gradient-to-r from-[#0078D4] to-[#00A4B4] text-white py-3 rounded-lg hover:scale-105 transition-transform animate-pulse"
          disabled={isGenerating}
        >
          {isGenerating ? 'Please Wait' : 'Generate Prompt'}
        </button>
      </form>
    </section>
  );
}

function Footer({ setIsPrivacyModalOpen }) {
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);
  
  return (
    <footer className="bg-gray-800 text-white py-4 mt-12">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <p>© 2025 CoPilot Prompt Generator. All rights reserved.</p>
        {(!user || user.tier === 'free') && (
          <p>Advertisement Pending</p>
        )}
        <a href="mailto:info@prompteh.com?subject=CoPilot%20Prompt%20Generator%20Feedback" className="text-gray-300 hover:text-white mx-2">Contact</a>
        <Link to="/privacy-policy" className="text-gray-300 hover:text-white mx-2 bg-transparent border-none cursor-pointer p-0 text-left">Privacy Policy</Link>
        <div className="mt-2">
          <a 
            href="https://x.com/prompt_eh" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="mx-2"
            onClick={(e) => {
              e.preventDefault();
              window.open('https://x.com/prompt_eh', '_blank', 'noopener,noreferrer');
            }}
          >
            <SocialIcon url="https://x.com/prompt_eh" style={{ height: 30, width: 30 }} />
          </a>
          <a 
            href="https://www.linkedin.com/company/107094288" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="mx-2"
            onClick={(e) => {
              e.preventDefault();
              window.open('https://www.linkedin.com/company/107094288', '_blank', 'noopener,noreferrer');
            }}
          >
            <SocialIcon url="https://www.linkedin.com/company/107094288" style={{ height: 30, width: 30 }} />
          </a>
        </div>
      </div>
    </footer>
  );
}

function IntroVideo({ onSkip }) {
  return (
    <div className="fixed inset-0 z-50 bg-black">
      <div className="relative w-full h-full">
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          onEnded={onSkip}
          onClick={onSkip}
          src="https://prompteh.servethe.click/intro.mp4"
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSkip();
          }}
          className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-all duration-200"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

function Advertisement() {
  return (
    <div className="ad-container my-4 mx-auto max-w-4xl">
      {/* Placeholder for advertisement */}
      <div className="min-h-[100px] flex items-center justify-center bg-gray-100 rounded">
        <p className="text-gray-500">Advertisement Pending</p>
      </div>
    </div>
  );
}

function App() {
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('prompt');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [showIntro, setShowIntro] = useState(true);
  const [user, setUser] = useState(null);
  const [showAuthOptions, setShowAuthOptions] = useState(false);

  useEffect(() => {
    // Skip intro and auth checks if we're on the privacy policy page
    if (window.location.pathname === '/privacy-policy') {
      setShowIntro(false);
      setShowAuthOptions(false);
      return;
    }
    
    // Load user data if available
    const userData = localStorage.getItem('user');
    if (userData) {
      // If user exists, skip intro and auth options
      setUser(JSON.parse(userData));
      setShowIntro(false);
      setShowAuthOptions(false);
      console.log('User data found, skipping intro and auth options');
    } else {
      // If no user, show intro first then auth options
      const introSeen = localStorage.getItem('introSeen');
      if (introSeen) {
        // If intro was seen but no user exists yet, show auth options
        setShowIntro(false);
        setShowAuthOptions(true);
        console.log('Intro seen, showing auth options');
      } else {
        console.log('No user data, showing intro');
      }
    }
  }, []);

  // Handle intro completion - now shows auth options
  const handleIntroComplete = () => {
    setShowIntro(false);
    setShowAuthOptions(true);
    localStorage.setItem('introSeen', 'true');
  };

  const handleGenerate = async (formData, onComplete) => {
    try {
      const response = await fetch('https://api.prompteh.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY || 'your-api-key'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to generate prompt');
      }
      
      setGeneratedPrompt(data.prompt);
      setIsPromptModalOpen(true);
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert(`Error: ${error.message}`);
    } finally {
      if (onComplete) onComplete();
    }
  };

  const handleClosePromptModal = () => {
    setIsPromptModalOpen(false);
  };

  const handleAgentSubmit = (generatedText) => {
    setGeneratedPrompt(generatedText);
    setIsPromptModalOpen(true);
  };

  const onLoginSuccess = (response) => {
    console.log('Login success:', response);
    const { code } = response;
    if (code) {
      fetch('https://auth.prompteh.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.error) {
            throw new Error(data.error_description || data.error);
          }
          localStorage.setItem('auth_token', data.token);
          return fetch('https://auth.prompteh.com/me', {
            headers: {
              'Authorization': `Bearer ${data.token}`,
            },
          });
        })
        .then(response => response.json())
        .then(userData => {
          if (userData.error) {
            throw new Error(userData.error_description || userData.error);
          }
          localStorage.setItem('user', JSON.stringify(userData));
          window.location.href = '/';
        })
        .catch(error => {
          console.error('Authentication error after receiving code:', error);
          window.location.href = `/login?error=token_exchange_failed&error_description=${encodeURIComponent(error.message)}`;
        });
    }
  };

  const onLoginFailure = (response) => {
    console.error('Login failure:', response);
    window.location.href = `/login?error=${encodeURIComponent(response.error)}&error_description=${encodeURIComponent(response.error_description || 'Login failed.')}`;
  };

  return (
    <Router>
      <div className={`min-h-screen flex flex-col ${isPromptModalOpen ? 'filter blur-sm' : ''}`}>
        <Helmet>
          <meta charSet="utf-8" />
          <title>CoPilot Prompt Generator</title>
          <meta name="description" content="Generate effective prompts for Microsoft Copilot" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href="/favicon.ico" />
          <meta property="og:title" content="CoPilot Prompt Generator" />
          <meta property="og:description" content="Create effective prompts for Microsoft Copilot with our easy-to-use generator." />
          <meta property="og:image" content="https://www.prompteh.com/og-image.jpg" />
          <meta property="og:url" content="https://www.prompteh.com" />
          <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
        
        <Routes>
          {/* Privacy Policy route - direct access, no checks */}
          <Route path="/privacy-policy" element={
            <>
              <Header />
              <PrivacyPolicy />
              <Footer setIsPrivacyModalOpen={setIsPrivacyModalOpen} />
            </>
          } />
          
          {/* All other routes - normal flow with intro/auth checks */}
          <Route path="*" element={
            <>
              {/* First priority: Show intro if needed */}
              {showIntro && <IntroVideo onSkip={handleIntroComplete} />}
              
              {/* Second priority: Show auth options if intro is done but no user yet */}
              {!showIntro && showAuthOptions && <AuthOptions />}
              
              {/* Third priority: Show main app if auth is complete */}
              {!showIntro && !showAuthOptions && (
                <>
                  <Header />
                  <Routes>
                    <Route path="/login" element={<Login onSuccess={onLoginSuccess} onFailure={onLoginFailure} />} />
                    <Route path="/callback" element={<Callback />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/free-tier" element={<FreeTier />} />
                    <Route path="/createaccount" element={<CreateAccount />} />
                    <Route path="/" element={
                      <main className="flex-grow max-w-7xl mx-auto px-4 py-12 w-full">
                        {/* Show ad for free users */}
                        {(!user || user.tier === 'free') && <Advertisement />}
                        
                        <section className="text-center mb-12">
                          <h2 className="text-3xl font-bold mb-3">CoPilot Prompt Generator</h2>
                          <p className="text-gray-600 max-w-2xl mx-auto">
                            Create effective prompts for Microsoft Copilot to help you accomplish your tasks more efficiently.
                          </p>
                        </section>
                        
                        <div className="tabs flex justify-center mb-6">
                          <button 
                            className={`px-4 py-2 rounded-lg mr-2 ${activeTab === 'prompt' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                            onClick={() => setActiveTab('prompt')}
                          >
                            Prompt Generator
                          </button>
                          <button 
                            className={`px-4 py-2 rounded-lg ${activeTab === 'agent' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                            onClick={() => setActiveTab('agent')}
                          >
                            Agent Generator
                          </button>
                        </div>
                        
                        {activeTab === 'prompt' && <PromptForm onGenerate={handleGenerate} />}
                        {activeTab === 'agent' && <AgentForm onSubmit={handleAgentSubmit} />}
                      </main>
                    } />
                  </Routes>
                  <Footer setIsPrivacyModalOpen={setIsPrivacyModalOpen} />
                </>
              )}
            </>
          } />
        </Routes>
        
        {isPromptModalOpen && (
          <GeneratePromptModal 
            prompt={generatedPrompt} 
            onClose={handleClosePromptModal} 
            isFreeTier={!user || user.tier === 'free'}
            adLink="https://otieu.com/4/9328332"
          />
        )}
        
        {isPrivacyModalOpen && (
          <PrivacyPolicyModal 
            isOpen={isPrivacyModalOpen}
            onClose={() => setIsPrivacyModalOpen(false)} 
          />
        )}
      </div>
    </Router>
  );
}

export default App;